import LoadMoreButton from '@atoms/LoadMoreButton/LoadMoreButton';
import ShadowContainerHorizontal from '@atoms/ShadowContainerHorizontal/ShadowContainerHorizontal';
import Tag from '@atoms/Tag/Tag';
import { ITechnicalSpecification } from '@models/ITechnicalSpecification';
import { ITechnicalSpecificationItem } from '@models/ITechnicalSpecificationItem';
import TagTypesEnum from '@models/enums/TagTypesEnum';
import SoftLoadWrapper from '@molecules/SoftLoadWrapper/SoftLoadWrapper';
import TechnicalSpecificationItem from '@molecules/TechnicalSpecificationItem/TechnicalSpecificationItem';
import useProductPageContentStore from '@state/ProductPageStore';
import updateDataLayer from '@utils/helpers/gtm';
import useIsTouchDevice from '@utils/hooks/useIsTouchDevice';
import { useState } from 'react';

const TechnicalSpecification = (props: ITechnicalSpecification) => {
  const {
    anchorId = '',
    defaultCount = 8,
    tabs,
    loadLessLabel,
    loadMoreLabel,
    meta,
    readLessLabel,
    readMoreLabel,
    title,
  } = useProductPageContentStore().productPageContent?.technicalSpecification ||
  props;

  const [open, setOpen] = useState(false);
  const isTouchDevice = useIsTouchDevice();
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const currentTab = tabs[activeTabIndex];

  const scrollTo = (id: string) => {
    const element = document.getElementById(id);
    const y =
      (element?.getBoundingClientRect().top || 0) +
      window.scrollY -
      (isTouchDevice ? 180 : 150);
    window.scrollTo({ top: y, behavior: 'smooth' });
  };

  const hasItems = tabs?.filter((tab) => !!tab.items?.length);

  if (!hasItems.length) {
    // If there are no items in any tab, return null to avoid rendering the section
    return null;
  }

  return (
    <section
      id={`a-${anchorId ?? 'tech-specs'}`}
      className="component-padding bg-white text-grey-600 dark:bg-black-100 dark:text-white"
      data-testid="CT-16-Technical-Specification"
    >
      <SoftLoadWrapper>
        <div className="container grid w-full grid-flow-row lg:grid-flow-col lg:grid-cols-8 xl:grid-cols-12">
          {title && (
            <h2
              className="col-span-full mb-10 lg:mb-13"
              data-epi-property-name={meta?.editHints?.title}
              data-testid="CT-16-Technical-Specification-Title"
            >
              {title}
            </h2>
          )}
          {tabs.length > 1 && (
            <ShadowContainerHorizontal
              hasTabbableNestedItems
              className="scrollbar-hide col-span-full mb-10 overflow-x-scroll lg:mb-13"
            >
              <div className="flex gap-5">
                {tabs.map((tab, index) => {
                  const isActive = activeTabIndex === index;
                  return (
                    <Tag
                      className="shrink-0 whitespace-nowrap"
                      key={`tag-tmc-${tab.title}`}
                      tagType={
                        isActive ? TagTypesEnum.Secondary : TagTypesEnum.Primary
                      }
                      text={tab.title}
                      pointerStates={!isActive}
                      onClick={() => {
                        setActiveTabIndex(index);
                      }}
                      dataTestId={`CT-16-Technical-Specification-Tab-${index}`}
                    />
                  );
                })}
              </div>
            </ShadowContainerHorizontal>
          )}

          {currentTab && !!currentTab.items?.length && (
            <div
              data-epi-property-name={meta?.editHints?.items}
              className="col-span-full"
              data-testid="CT-16-Technical-Specification-Items"
            >
              {currentTab.items
                .slice(0, !open ? defaultCount : currentTab.items.length)
                .map((item: ITechnicalSpecificationItem, index) => (
                  <TechnicalSpecificationItem
                    dataTestId={`CT-16-Technical-Specification-Item-${index}`}
                    key={item.title}
                    {...item}
                    showIcons={currentTab.items!.every((obj) => obj.icon)} // only show icons if all icons are populated
                    readMoreLabel={readMoreLabel}
                    readLessLabel={readLessLabel}
                  />
                ))}
            </div>
          )}
          {currentTab &&
            currentTab.items &&
            currentTab.items.length > defaultCount && (
              <div className="flew-row col-span-full mt-9 flex w-full justify-center overflow-hidden lg:mt-15">
                <LoadMoreButton
                  loadLessLabel={loadLessLabel}
                  loadMoreLabel={loadMoreLabel}
                  open={open}
                  onClick={() => {
                    if (open) {
                      scrollTo(`a-${anchorId ?? 'tech-specs'}`);
                    }
                    setOpen(!open);
                    updateDataLayer({
                      event: 'selectContent',
                      content_type: 'technical_specifications',
                      link_text: 'load_more',
                    });
                  }}
                  dataTestId="CT-16-Technical-Specification-Load-More-Button"
                />
              </div>
            )}
        </div>
      </SoftLoadWrapper>
    </section>
  );
};

export default TechnicalSpecification;
